import './WidgetStyle.css'; 
import './ResponsiveFonts.css';
import { useFont } from '../../Design/FontContext';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import DeleteModal from './DeleteModal';
import { getTextEffectStyles } from '../utils/textEffectsUtils';
import { getButtonEffectStyles } from '../utils/buttonEffectsUtils';
import '../WidgetEditComponents/TextEffectsAnimations.css';
import './MobileBreakpoints.css';
import { generateLayoutStyles, getResponsiveValue, appendPx } from '../WidgetEditComponents/LayoutUtils';

function FancyFooter({ data, onRemove, isSelected, isEditMode, onUpdate, onSelectWidget, pageData }) {
    const deleteLogo = '/img/delete.svg';
    const defaultLogo = '/img/your-logo.webp';
    const fonts = useFont();
    
    // INLINE-EDITING SYSTEM
    const [isInlineEditing, setIsInlineEditing] = useState(null);
    const [inlineValue, setInlineValue] = useState('');
    const [originalLayout, setOriginalLayout] = useState({});
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    
    // Mobile Layout Detection
    const [isMobileLayout, setIsMobileLayout] = useState(false);
    const containerRef = useRef(null);
    
    // Ref für Outside-Click Detection
    const widgetRef = useRef(null);

    // Layout Configuration
    const getLayoutConfig = () => {
        const layoutType = data.layoutType || 'corporate-layout';
        
        const layouts = {
            'corporate-layout': {
                // Vollständiger Corporate Footer
                showLogo: true,
                showContactInfo: true,
                showNavigation: true,
                showSocialLinks: true,
                showBottomBar: true,
                direction: 'row',
                alignment: 'flex-start',
                logoPosition: 'left',
                columnsPerRow: 4,
                spacing: 'normal',
                style: 'professional'
            },
            'modern-layout': {
                // Modern mit Logo zentriert, ohne Contact
                showLogo: true,
                showContactInfo: false,
                showNavigation: true,
                showSocialLinks: true,
                showBottomBar: true,
                direction: 'column',
                alignment: 'center',
                logoPosition: 'center',
                columnsPerRow: 3,
                spacing: 'wide',
                style: 'modern'
            },
            'minimal-layout': {
                // NUR Bottom Bar - extrem minimal
                showLogo: false,
                showContactInfo: false,
                showNavigation: false,
                showSocialLinks: false,
                showBottomBar: true,
                direction: 'row',
                alignment: 'center',
                logoPosition: 'center',
                columnsPerRow: 1,
                spacing: 'compact',
                style: 'minimal'
            },
            'creative-layout': {
                // Künstlerisch: Logo + Social, keine Navigation
                showLogo: true,
                showContactInfo: true,
                showNavigation: false,
                showSocialLinks: true,
                showBottomBar: false,
                direction: 'column',
                alignment: 'center',
                logoPosition: 'center',
                columnsPerRow: 2,
                spacing: 'creative',
                style: 'creative'
            }
        };
        
        return layouts[layoutType] || layouts['corporate-layout'];
    };

    const layoutConfig = getLayoutConfig();

    // Delete Handler
    const handleDelete = (event) => {
        event.stopPropagation();
        if (onRemove) {
            onRemove({ stopPropagation: () => {} }, data.id);
        }
        setShowDeleteModal(false);
    };

    // Mobile Breakpoint Detection (768px für Footer)
    useEffect(() => {
        const checkContainerWidth = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                setIsMobileLayout(containerWidth < 768);
            }
        };

        checkContainerWidth();
        window.addEventListener('resize', checkContainerWidth);
        
        const observer = new ResizeObserver(checkContainerWidth);
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            window.removeEventListener('resize', checkContainerWidth);
            observer.disconnect();
        };
    }, []);

    // INLINE-EDITING FUNCTIONS
    const handleInlineEdit = (fieldName, currentValue) => {
        if (!isEditMode || !onUpdate) return;
        
        // Widget selektieren wenn nicht bereits selektiert
        if (!isSelected && onSelectWidget) {
            onSelectWidget(data.id);
        }
        
        if (isInlineEditing === fieldName) {
            handleInlineCancel();
            return;
        }
        setIsInlineEditing(fieldName);
        setInlineValue(currentValue || '');
    };

    const handleInlineSubmit = useCallback((fieldName) => {
        if (!onUpdate) {
            console.warn('⚠️ onUpdate not available');
            return;
        }
        
        console.log('💾 FancyFooter Saving field:', fieldName, 'with value:', inlineValue);
        const updatedData = { ...data, [fieldName]: inlineValue };
        onUpdate(updatedData);
        setIsInlineEditing(null);
        setInlineValue('');
        console.log('✅ FancyFooter Save completed for:', fieldName);
    }, [onUpdate, data, inlineValue]);

    const handleInlineCancel = () => {
        setIsInlineEditing(null);
        setInlineValue('');
    };

    const handleInlineKeyPress = (e, fieldName) => {
        const isCurrentlyTextarea = e.target.tagName === 'TEXTAREA';
        
        if (e.key === 'Enter') {
            if (isCurrentlyTextarea) {
                if (e.ctrlKey || e.metaKey) {
                    e.preventDefault();
                    handleInlineSubmit(fieldName);
                }
            } else {
                e.preventDefault();
                handleInlineSubmit(fieldName);
            }
        } else if (e.key === 'Escape') {
            handleInlineCancel();
        }
    };

    // Smart Outside-Click Handler
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!isInlineEditing) return;
            
            const isEditingInput = event.target.tagName === 'TEXTAREA' || event.target.tagName === 'INPUT';
            const isActionButton = event.target.hasAttribute('data-action-button');
            const isDeleteButton = event.target.closest('.delete_button');
            const isWidgetEdit = event.target.closest('.widget-edit');
            const isAddWidgets = event.target.closest('.add-widgets');
            const isPagePreview = event.target.closest('.page_preview');
            
            const isRealOutsideClick = !isEditingInput && 
                                     !isActionButton && 
                                     !isDeleteButton && 
                                     !isWidgetEdit &&
                                     (isPagePreview || !widgetRef.current?.contains(event.target));
            
            if (isRealOutsideClick) {
                if (onUpdate && inlineValue !== undefined) {
                    const updatedData = { ...data, [isInlineEditing]: inlineValue };
                    onUpdate(updatedData);
                }
                setIsInlineEditing(null);
                setInlineValue('');
            }
        };

        if (isInlineEditing) {
            document.addEventListener('mousedown', handleClickOutside, true);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside, true);
            };
        }
    }, [isInlineEditing, inlineValue, data, onUpdate]);

    // RENDER EDITABLE TEXT FUNCTION
    const renderEditableText = (fieldName, currentValue, Element = 'p', style = {}, placeholder = 'Click to edit...') => {
        if (!isEditMode) {
            return currentValue ? (
                <Element style={{
                    ...style,
                    whiteSpace: style.whiteSpace || 'pre-wrap'
                }}>
                    {currentValue}
                </Element>
            ) : null;
        }

        if (isInlineEditing === fieldName) {
            const layoutData = originalLayout;
            const isTextarea = Element !== 'button' && (fieldName.includes('address') || fieldName.includes('description'));
            
            // Action Buttons Container
            const renderActionButtons = () => (
                <div style={{
                    position: 'absolute',
                    top: '-11px',
                    right: '-6px',
                    display: 'flex',
                    gap: '4px',
                    zIndex: 9999,
                    background: 'rgba(255, 255, 255, 0.95)',
                    padding: '2px 4px',
                    borderRadius: '6px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    backdropFilter: 'blur(4px)',
                    border: '1px solid rgba(0, 0, 0, 0.05)'
                }}>
                    <button
                        data-action-button="save"
                        onClick={() => handleInlineSubmit(fieldName)}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            padding: '0',
                            fontSize: '9px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            height: '16px',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(16, 185, 129, 0.3)'
                        }}
                        title="Save"
                    >
                        ✓
                    </button>
                    
                    <button
                        data-action-button="cancel"
                        onClick={handleInlineCancel}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            padding: '0',
                            fontSize: '9px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            height: '16px',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(239, 68, 68, 0.3)'
                        }}
                        title="Cancel"
                    >
                        ×
                    </button>
                </div>
            );
            
            return isTextarea ? (
                <div style={{ 
                    position: 'relative',
                    width: '100%'
                }}>
                    <textarea
                        value={inlineValue}
                        onChange={(e) => {
                            setInlineValue(e.target.value);
                            const target = e.target;
                            const cursorPosition = target.selectionStart;
                            setTimeout(() => {
                                target.style.height = 'auto';
                                target.style.height = target.scrollHeight + 'px';
                                target.setSelectionRange(cursorPosition, cursorPosition);
                            }, 0);
                        }}
                        onKeyDown={(e) => handleInlineKeyPress(e, fieldName)}
                        onBlur={() => handleInlineSubmit(fieldName)}
                        placeholder={placeholder}
                        autoFocus
                        style={{
                            ...style,
                            background: 'transparent',
                            border: 'none',
                            outline: 'none',
                            resize: 'none',
                            padding: '4px 8px',
                            margin: '0',
                            width: '100%',
                            color: '#333333',
                            backgroundImage: 'none',
                            fontFamily: style.fontFamily,
                            fontSize: style.fontSize,
                            fontWeight: style.fontWeight,
                            lineHeight: style.lineHeight,
                            textAlign: style.textAlign || 'inherit',
                            whiteSpace: 'pre-wrap'
                        }}
                    />
                    {renderActionButtons()}
                </div>
            ) : (
                <div style={{ position: 'relative', width: '100%' }}>
                    <input
                        type="text"
                        value={inlineValue}
                        onChange={(e) => setInlineValue(e.target.value)}
                        onKeyDown={(e) => handleInlineKeyPress(e, fieldName)}
                        onBlur={() => handleInlineSubmit(fieldName)}
                        placeholder={placeholder}
                        autoFocus
                        style={{
                            ...style,
                            background: 'transparent',
                            border: 'none',
                            outline: 'none',
                            padding: '4px 8px',
                            margin: '0',
                            width: '100%',
                            color: '#333333',
                            fontFamily: style.fontFamily,
                            fontSize: style.fontSize,
                            fontWeight: style.fontWeight,
                            textAlign: style.textAlign || 'inherit'
                        }}
                    />
                    {renderActionButtons()}
                </div>
            );
        }

        // Edit-Modus: Inline-Editing ermöglichen
        const handleElementClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            const rect = e.target.getBoundingClientRect();
            const computedStyle = window.getComputedStyle(e.target);
            
            const layoutData = {
                width: rect.width + 'px',
                height: rect.height + 'px'
            };
            
            setOriginalLayout(layoutData);
            handleInlineEdit(fieldName, currentValue);
        };

        return (
            <Element 
                style={{
                    ...style,
                    cursor: 'text',
                    position: 'relative',
                    transition: 'all 0.2s ease',
                    padding: style.padding || '4px 8px', // Respektiert explizites Padding
                    borderRadius: '4px'
                }}
                onClick={handleElementClick}
                title="Click to edit"
            >
                {currentValue || placeholder}
                {/* Blinkender Cursor beim Editieren */}
                {isEditMode && isInlineEditing === fieldName && (
                    <span style={{
                        display: 'inline',
                        animation: 'blink 1s infinite',
                        fontSize: 'inherit',
                        color: 'inherit',
                        marginLeft: '1px'
                    }}>
                        |
                    </span>
                )}
            </Element>
        );
    };

    // Image Upload Function
    const handleImageUpload = (fieldName) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = (e) => {
            const file = e.target.files[0];
            if (file && onUpdate) {
                const fakeUrl = URL.createObjectURL(file);
                const updatedData = { ...data, [fieldName]: fakeUrl };
                onUpdate(updatedData);
            }
        };
        input.click();
    };

    // Widget Styling
    function getBorderStyle() {
        if (isEditMode) {
            if (isSelected) {
                return '2px dashed #000000';
            } else {
                return '2px dashed transparent';
            }
        } else {
            return data.borderTop ? `${data.borderTopWidth}px solid ${data.borderTopColor}` : 'none';
        }
    }

    // 🚀 REVOLUTIONARY LAYOUT SYSTEM - Responsive Widget Styles
    const layoutStyles = generateLayoutStyles(data, isMobileLayout, {
        backgroundColor: data.backgroundColor || '#1a1a1a',
        color: data.textColor || '#666666',
        borderTop: getBorderStyle(),
        position: 'relative',
        width: '100%',
        maxWidth: 'none'
    });

    const widgetStyle = {
        ...layoutStyles,
        // Fallback für alte Height-Property (Backwards Compatibility)
        ...(layoutStyles.height === 'auto' && !layoutStyles.minHeight && {
            minHeight: appendPx(data.height || 0)
        })
    };

    // Get Social Media Icon (FontAwesome Classes)
    const getSocialIcon = (platform) => {
        const platformLower = platform.toLowerCase();
        
        // FontAwesome Icon mapping für verschiedene Platforms
        const iconMap = {
            'facebook': 'fab fa-facebook-f',
            'twitter': 'fab fa-twitter',
            'x': 'fab fa-x-twitter',
            'instagram': 'fab fa-instagram',
            'linkedin': 'fab fa-linkedin-in',
            'youtube': 'fab fa-youtube',
            'tiktok': 'fab fa-tiktok',
            'pinterest': 'fab fa-pinterest-p',
            'snapchat': 'fab fa-snapchat-ghost',
            'discord': 'fab fa-discord',
            'telegram': 'fab fa-telegram-plane',
            'whatsapp': 'fab fa-whatsapp',
            'github': 'fab fa-github',
            'reddit': 'fab fa-reddit-alien',
            'tumblr': 'fab fa-tumblr',
            'twitch': 'fab fa-twitch',
            'spotify': 'fab fa-spotify',
            'soundcloud': 'fab fa-soundcloud',
            'vimeo': 'fab fa-vimeo-v',
            'behance': 'fab fa-behance',
            'dribbble': 'fab fa-dribbble',
            'medium': 'fab fa-medium-m',
            'slack': 'fab fa-slack',
            'skype': 'fab fa-skype',
            'zoom': 'fas fa-video',
            'email': 'fas fa-envelope',
            'website': 'fas fa-globe',
            'blog': 'fas fa-blog'
        };
        
        // Versuche exakte Übereinstimmung
        if (iconMap[platformLower]) {
            return iconMap[platformLower];
        }
        
        // Versuche Teilübereinstimmung
        for (const [key, icon] of Object.entries(iconMap)) {
            if (platformLower.includes(key) || key.includes(platformLower)) {
                return icon;
            }
        }
        
        // Fallback für unbekannte Platforms
        return 'fas fa-link';
    };

    // Get Platform Color
    const getPlatformColor = (platform) => {
        const platformLower = platform.toLowerCase();
        
        const colorMap = {
            'facebook': '#1877F2',
            'twitter': '#1DA1F2',
            'x': '#000000',
            'instagram': '#E4405F',
            'linkedin': '#0A66C2',
            'youtube': '#FF0000',
            'tiktok': '#000000',
            'pinterest': '#BD081C',
            'snapchat': '#FFFC00',
            'discord': '#5865F2',
            'telegram': '#26A5E4',
            'whatsapp': '#25D366',
            'github': '#181717',
            'reddit': '#FF4500',
            'tumblr': '#00CF35',
            'twitch': '#9146FF',
            'spotify': '#1DB954',
            'soundcloud': '#FF3300',
            'vimeo': '#1AB7EA',
            'behance': '#1769FF',
            'dribbble': '#EA4C89',
            'medium': '#000000',
            'slack': '#4A154B',
            'skype': '#00AFF0',
            'zoom': '#2D8CFF',
            'email': '#EA4335',
            'website': '#4285F4',
            'blog': '#FF6B35'
        };
        
        return colorMap[platformLower] || (data.accentColor || '#5C80BC');
    };

    // Get Social Icon Style Configuration
    const getSocialIconStyles = (platform, iconStyle) => {
        const platformColor = getPlatformColor(platform);
        const baseStyles = {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            transition: 'all 0.3s ease',
            textDecoration: 'none'
        };

        const iconBaseStyles = {
            fontSize: '20px',
            transition: 'all 0.3s ease'
        };

        switch (iconStyle) {
            case 'brand-colors':
                return {
                    container: {
                        ...baseStyles,
                        backgroundColor: platformColor,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                    },
                    icon: {
                        ...iconBaseStyles,
                        color: '#ffffff'
                    },
                    hover: {
                        backgroundColor: data.linkHoverColor || '#000000', // ✅ FIXED: Schwarzer Fallback für bessere Lesbarkeit
                        iconColor: platformColor
                    }
                };

            case 'outline-dark':
                return {
                    container: {
                        ...baseStyles,
                        backgroundColor: 'transparent',
                        border: `2px solid #1f2937`,
                        boxShadow: 'none'
                    },
                    icon: {
                        ...iconBaseStyles,
                        color: '#1f2937'
                    },
                    hover: {
                        backgroundColor: '#1f2937',
                        iconColor: '#ffffff'
                    }
                };

            case 'outline-light':
                return {
                    container: {
                        ...baseStyles,
                        backgroundColor: 'transparent',
                        border: `2px solid rgba(255, 255, 255, 0.6)`,
                        boxShadow: 'none'
                    },
                    icon: {
                        ...iconBaseStyles,
                        color: 'rgba(255, 255, 255, 0.8)'
                    },
                    hover: {
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        iconColor: '#1f2937'
                    }
                };

            case 'minimal-dark':
                return {
                    container: {
                        ...baseStyles,
                        backgroundColor: '#f3f4f6',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                    },
                    icon: {
                        ...iconBaseStyles,
                        color: '#374151'
                    },
                    hover: {
                        backgroundColor: '#e5e7eb',
                        iconColor: '#111827'
                    }
                };

            case 'minimal-light':
                return {
                    container: {
                        ...baseStyles,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        boxShadow: 'none'
                    },
                    icon: {
                        ...iconBaseStyles,
                        color: 'rgba(255, 255, 255, 0.7)'
                    },
                    hover: {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        iconColor: '#ffffff'
                    }
                };

            case 'gradient-modern':
                return {
                    container: {
                        ...baseStyles,
                        background: `linear-gradient(135deg, ${platformColor} 0%, ${platformColor}dd 100%)`,
                        boxShadow: `0 4px 15px ${platformColor}33`
                    },
                    icon: {
                        ...iconBaseStyles,
                        color: '#ffffff'
                    },
                    hover: {
                        background: `linear-gradient(135deg, ${platformColor}dd 0%, ${platformColor}aa 100%)`,
                        iconColor: '#ffffff'
                    }
                };

            default:
                return {
                    container: {
                        ...baseStyles,
                        backgroundColor: platformColor,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                    },
                    icon: {
                        ...iconBaseStyles,
                        color: '#ffffff'
                    },
                    hover: {
                        backgroundColor: data.linkHoverColor || '#000000', // ✅ FIXED: Schwarzer Fallback für bessere Lesbarkeit
                        iconColor: platformColor
                    }
                };
        }
    };

    // Render Social Links
    const renderSocialLinks = () => {
        if (!data.showSocialLinks || !data.socialLinks) return null;

        const iconStyle = data.socialIconStyle || 'brand-colors';

        return (
            <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: layoutConfig.alignment === 'center' ? 'center' : 'flex-start',
                flexWrap: 'wrap'
            }}>
                {data.socialLinks.map((social, index) => {
                    const socialIcon = getSocialIcon(social.platform);
                    const styles = getSocialIconStyles(social.platform, iconStyle);
                    
                    return (
                        <a
                            key={index}
                            href={social.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={styles.container}
                            onMouseEnter={(e) => {
                                if (styles.hover.background) {
                                    e.target.style.background = styles.hover.background;
                                } else {
                                    e.target.style.backgroundColor = styles.hover.backgroundColor;
                                }
                                e.target.style.transform = 'translateY(-3px)';
                                e.target.style.boxShadow = iconStyle === 'gradient-modern' ? 
                                    `0 6px 20px ${getPlatformColor(social.platform)}44` :
                                    '0 4px 16px rgba(0, 0, 0, 0.2)';
                                
                                // Icon Farbe bei Hover anpassen
                                const iconElement = e.target.querySelector('.social-icon');
                                if (iconElement) {
                                    iconElement.style.color = styles.hover.iconColor;
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (styles.container.background) {
                                    e.target.style.background = styles.container.background;
                                } else {
                                    e.target.style.backgroundColor = styles.container.backgroundColor;
                                }
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = styles.container.boxShadow || 'none';
                                
                                // Icon Farbe zurücksetzen
                                const iconElement = e.target.querySelector('.social-icon');
                                if (iconElement) {
                                    iconElement.style.color = styles.icon.color;
                                }
                            }}
                            title={`Visit us on ${social.platform.charAt(0).toUpperCase() + social.platform.slice(1)}`}
                        >
                            <i 
                                className={`${socialIcon} social-icon`}
                                style={styles.icon}
                            />
                        </a>
                    );
                })}
            </div>
        );
    };

    // Scroll to Widget Function
    const scrollToWidget = (targetWidgetId) => {
        if (!targetWidgetId || isEditMode) return;
        
        // Find the widget element by ID
        const targetElement = document.querySelector(`[data-widget-id="${targetWidgetId}"]`);
        if (targetElement) {
            targetElement.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'start',
                inline: 'nearest'
            });
        } else {
            console.warn('Widget not found for scrolling:', targetWidgetId);
        }
    };

    // Handle Link Click
    const handleLinkClick = (e, link) => {
        if (link.type === 'widget' && link.targetWidgetId) {
            e.preventDefault();
            scrollToWidget(link.targetWidgetId);
        } else if (link.type === 'url' && link.url && link.url !== '#') {
            // Normal URL handling - let the browser handle it
            if (link.url.startsWith('http')) {
                e.preventDefault();
                window.open(link.url, '_blank', 'noopener,noreferrer');
            }
        } else {
            e.preventDefault(); // Prevent empty links
        }
    };

    // Render Navigation Categories
    const renderCategories = () => {
        if (!data.showNavigation || !data.categories) return null;

        return (
            <div style={{
                display: 'grid',
                gridTemplateColumns: isMobileLayout ? '1fr' : `repeat(${Math.min(layoutConfig.columnsPerRow, data.categories.length)}, 1fr)`,
                gap: `${data.columnGap || 40}px`,
                width: '100%'
            }}>
                {data.categories.map((category, index) => (
                    <div key={category.id || index} style={{ minWidth: '0' }}>
                        {/* Category Title - Custom Inline Edit für verschachtelte Properties */}
                        {isEditMode ? (
                            <div style={{ position: 'relative', marginBottom: '16px' }}>
                                {isInlineEditing === `category-title-${index}` ? (
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type="text"
                                            value={inlineValue}
                                            onChange={(e) => setInlineValue(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    // Update categories array
                                                    const updatedCategories = [...(data.categories || [])];
                                                    updatedCategories[index] = { ...updatedCategories[index], title: inlineValue };
                                                    const updatedData = { ...data, categories: updatedCategories };
                                                    onUpdate(updatedData);
                                                    setIsInlineEditing(null);
                                                    setInlineValue('');
                                                } else if (e.key === 'Escape') {
                                                    setIsInlineEditing(null);
                                                    setInlineValue('');
                                                }
                                            }}
                                            onBlur={() => {
                                                const updatedCategories = [...(data.categories || [])];
                                                updatedCategories[index] = { ...updatedCategories[index], title: inlineValue };
                                                const updatedData = { ...data, categories: updatedCategories };
                                                onUpdate(updatedData);
                                                setIsInlineEditing(null);
                                                setInlineValue('');
                                            }}
                                            autoFocus
                                            style={{
                                                color: data.headingColor || '#000000', // ✅ FIXED: Schwarzer Fallback für bessere Lesbarkeit
                                                fontSize: appendPx(data.headingSize || 18),
                                                fontWeight: data.headingWeight || '600',
                                                fontFamily: data.headingFont || fonts.globalBigFont,
                                                background: 'transparent',
                                                border: 'none',
                                                outline: 'none',
                                                padding: '4px 8px',
                                                width: '100%',
                                                color: '#333333'
                                            }}
                                        />
                                        {/* Action Buttons */}
                                        <div style={{
                                            position: 'absolute',
                                            top: '-11px',
                                            right: '-6px',
                                            display: 'flex',
                                            gap: '4px',
                                            zIndex: 9999,
                                            background: 'rgba(255, 255, 255, 0.95)',
                                            padding: '2px 4px',
                                            borderRadius: '6px',
                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                                        }}>
                                            <button
                                                data-action-button="save"
                                                onClick={() => {
                                                    const updatedCategories = [...(data.categories || [])];
                                                    updatedCategories[index] = { ...updatedCategories[index], title: inlineValue };
                                                    const updatedData = { ...data, categories: updatedCategories };
                                                    onUpdate(updatedData);
                                                    setIsInlineEditing(null);
                                                    setInlineValue('');
                                                }}
                                                style={{
                                                    background: '#10b981',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '50%',
                                                    width: '16px',
                                                    height: '16px',
                                                    fontSize: '9px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                ✓
                                            </button>
                                            <button
                                                data-action-button="cancel"
                                                onClick={() => {
                                                    setIsInlineEditing(null);
                                                    setInlineValue('');
                                                }}
                                                style={{
                                                    background: '#ef4444',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '50%',
                                                    width: '16px',
                                                    height: '16px',
                                                    fontSize: '9px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <h4
                                        style={{
                                            ...((!data.headingEffect || data.headingEffect === 'none') && {
                                                color: data.headingColor || '#000000' // ✅ FIXED: Schwarzer Fallback für bessere Lesbarkeit
                                            }),
                                            fontSize: appendPx(data.headingSize || 18),
                                            fontWeight: data.headingWeight || '600',
                                            fontFamily: data.headingFont || fonts.globalBigFont,
                                            marginBottom: '16px',
                                            lineHeight: '1.3',
                                            cursor: 'text',
                                            padding: '0',
                                            margin: '0 0 16px 0',
                                            borderRadius: '4px',
                                            transition: 'all 0.2s ease',
                                            ...getTextEffectStyles(data.headingEffect)
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (!isSelected && onSelectWidget) {
                                                onSelectWidget(data.id);
                                            }
                                            setIsInlineEditing(`category-title-${index}`);
                                            setInlineValue(category.title || '');
                                        }}
                                        title="Click to edit category title"
                                    >
                                        {category.title || 'Category Title'}
                                        {isInlineEditing === `category-title-${index}` && (
                                            <span style={{
                                                display: 'inline',
                                                animation: 'blink 1s infinite',
                                                marginLeft: '1px'
                                            }}>
                                                |
                                            </span>
                                        )}
                                    </h4>
                                )}
                            </div>
                        ) : (
                            <h4 style={{
                                color: data.headingColor || '#333333',
                                fontSize: appendPx(data.headingSize || 18),
                                fontWeight: data.headingWeight || '600',
                                fontFamily: data.headingFont || fonts.globalBigFont,
                                marginBottom: '16px',
                                lineHeight: '1.3',
                                padding: '0',
                                margin: '0 0 16px 0'
                            }}>
                                {category.title || 'Category Title'}
                            </h4>
                        )}
                        
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px'
                        }}>
                            {category.links && category.links.map((link, linkIndex) => (
                                <a
                                    key={linkIndex}
                                    href={link.type === 'url' ? link.url : '#'}
                                    onClick={(e) => handleLinkClick(e, link)}
                                    style={{
                                        color: data.linkColor || '#cccccc',
                                        fontSize: appendPx(data.bodySize || 14),
                                        fontWeight: data.bodyWeight || '400',
                                        fontFamily: data.bodyFont || fonts.globalSmallFont,
                                        textDecoration: 'none',
                                        transition: 'color 0.3s ease',
                                        lineHeight: '1.5',
                                        cursor: 'pointer',
                                        position: 'relative'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.color = data.linkHoverColor || '#000000' // ✅ FIXED: Schwarzer Fallback für bessere Lesbarkeit;
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.color = data.linkColor || '#cccccc';
                                    }}
                                    title={link.type === 'widget' ? 
                                        `Scroll to widget: ${link.text}` : 
                                        link.type === 'url' ? `Go to: ${link.url}` : link.text
                                    }
                                >
                                    {link.text}
                                </a>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    };


    return (
        <>
            {/* CSS Animations */}
            <style>
                {`
                    @keyframes blink {
                        0%, 50% { opacity: 1; }
                        51%, 100% { opacity: 0; }
                    }
                `}
            </style>
            
            <footer 
                className={`fancy-footer layout-${data.layoutType || 'corporate-layout'}`}
                style={widgetStyle}
                ref={(el) => {
                    containerRef.current = el;
                    widgetRef.current = el;
                }}
            >
                {/* Delete Button */}
                {isEditMode && (
                    <button className="delete_button" onClick={() => setShowDeleteModal(true)}>
                        <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                    </button>
                )}
                {isEditMode && showDeleteModal && (
                    <DeleteModal
                        isOpen={showDeleteModal}
                        onClose={() => setShowDeleteModal(false)}
                        onConfirm={(event) => handleDelete(event, data.id)}
                    />
                )}
                
                {/* Main Footer Content - Nur anzeigen wenn nicht minimal */}
                {layoutConfig.style !== 'minimal' && (
                    <div style={{
                        padding: `${data.paddingTop || 60}px ${data.paddingX || 40}px ${data.paddingBottom || 40}px`,
                        maxWidth: '1200px',
                        margin: '0 auto',
                        width: '100%'
                    }}>
                        {/* Top Section - Logo & Company Info */}
                        <div style={{
                            display: 'flex',
                            flexDirection: isMobileLayout ? 'column' : layoutConfig.direction,
                            alignItems: layoutConfig.alignment === 'center' ? 'center' : 'flex-start',
                            justifyContent: layoutConfig.alignment === 'center' ? 'center' : 'space-between',
                            marginBottom: (layoutConfig.showNavigation || layoutConfig.showSocialLinks) ? `${data.rowGap || 30}px` : '0',
                            gap: '30px'
                        }}>
                        {/* Logo & Branding Section */}
                        {(data.showLogo && layoutConfig.showLogo) && (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: layoutConfig.logoPosition === 'center' ? 'center' : 'flex-start',
                                gap: '16px',
                                maxWidth: isMobileLayout ? '100%' : '300px'
                            }}>
                                <div style={{ position: 'relative', display: 'inline-block' }}
                                    onMouseEnter={(e) => {
                                        if (isEditMode) {
                                            const uploadBtn = e.currentTarget.querySelector('.logo-upload-btn');
                                            if (uploadBtn) uploadBtn.style.opacity = '1';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (isEditMode) {
                                            const uploadBtn = e.currentTarget.querySelector('.logo-upload-btn');
                                            if (uploadBtn) uploadBtn.style.opacity = '0';
                                        }
                                    }}
                                >
                                    <img 
                                        src={data.logoImage || defaultLogo}
                                        alt="Logo"
                                        style={{
                                            width: appendPx(data.logoWidth || 120),
                                            height: appendPx(data.logoHeight || 60),
                                            objectFit: 'contain',
                                            filter: data.logoInvert ? 'invert(1)' : 'none',
                                            transition: 'filter 0.3s ease'
                                        }}
                                    />
                                    {/* Logo Control Buttons */}
                                    {isEditMode && (
                                        <>
                                            {/* Logo Upload Button */}
                                            <button
                                                onClick={() => handleImageUpload('logoImage')}
                                                style={{
                                                    position: 'absolute',
                                                    top: '-10px',
                                                    left: '-10px',
                                                    width: '32px',
                                                    height: '32px',
                                                    borderRadius: '50%',
                                                    background: 'var(--blue)',
                                                    color: 'white',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    opacity: 0,
                                                    transition: 'all 0.2s ease',
                                                    zIndex: 100,
                                                    boxShadow: '0 1px 3px rgba(99, 102, 241, 0.3)'
                                                }}
                                                title="Upload Logo"
                                                className="logo-upload-btn"
                                            >
                                                <img 
                                                    src="/img/upload.svg" 
                                                    alt="Upload" 
                                                    style={{
                                                        width: '18px',
                                                        height: '18px',
                                                        filter: 'invert(1)'
                                                    }}
                                                />
                                            </button>

                                            {/* Logo Color Invert Button */}
                                            <button
                                                onClick={() => {
                                                    if (onUpdate) {
                                                        const updatedData = { ...data, logoInvert: !data.logoInvert };
                                                        onUpdate(updatedData);
                                                    }
                                                }}
                                                style={{
                                                    position: 'absolute',
                                                    top: '-10px',
                                                    right: '-10px',
                                                    width: '32px',
                                                    height: '32px',
                                                    borderRadius: '50%',
                                                    background: data.logoInvert ? '#10b981' : '#6b7280',
                                                    color: 'white',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    opacity: 0,
                                                    transition: 'all 0.2s ease',
                                                    zIndex: 100,
                                                    boxShadow: data.logoInvert ? 
                                                        '0 1px 3px rgba(16, 185, 129, 0.3)' : 
                                                        '0 1px 3px rgba(107, 114, 128, 0.3)'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.background = data.logoInvert ? '#059669' : '#4b5563';
                                                    e.target.style.transform = 'scale(1.1)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.background = data.logoInvert ? '#10b981' : '#6b7280';
                                                    e.target.style.transform = 'scale(1)';
                                                }}
                                                title={data.logoInvert ? "Disable Color Invert" : "Enable Color Invert"}
                                                className="logo-upload-btn"
                                            >
                                                <span style={{
                                                    fontSize: '14px',
                                                    fontWeight: 'bold'
                                                }}>
                                                    {data.logoInvert ? '◐' : '○'}
                                                </span>
                                            </button>
                                        </>
                                    )}
                                </div>

                                {data.showCompanyName !== false && renderEditableText(
                                    'companyName',
                                    data.companyName,
                                    'h3',
                                    {
                                        color: data.headingColor || '#000000', // ✅ FIXED: Schwarzer Fallback für bessere Lesbarkeit
                                        fontSize: appendPx((data.headingSize || 18) + 4),
                                        fontWeight: '700',
                                        fontFamily: data.headingFont || fonts.globalBigFont,
                                        marginBottom: '8px',
                                        textAlign: layoutConfig.logoPosition === 'center' ? 'center' : 'left'
                                    },
                                    'Your Company'
                                )}

                                {data.showSlogan !== false && renderEditableText(
                                    'slogan',
                                    data.slogan,
                                    'p',
                                    {
                                        ...((!data.sloganEffect || data.sloganEffect === 'none') && {
                                            color: data.textColor || '#666666' // ✅ FIXED: Grauer Fallback statt weiß
                                        }),
                                        fontSize: appendPx(data.sloganSize || 16),
                                        fontWeight: data.sloganWeight || '400',
                                        fontFamily: data.bodyFont || fonts.globalSmallFont,
                                        lineHeight: '1.5',
                                        marginBottom: '0',
                                        opacity: 0.9,
                                        textAlign: layoutConfig.logoPosition === 'center' ? 'center' : 'left',
                                        ...getTextEffectStyles(data.sloganEffect)
                                    },
                                    'Your inspiring slogan here'
                                )}
                            </div>
                        )}

                        {/* Contact Information */}
                        {(data.showContactInfo && layoutConfig.showContactInfo) && (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '16px',
                                alignItems: layoutConfig.alignment === 'center' ? 'center' : 'flex-start',
                                maxWidth: isMobileLayout ? '100%' : '280px',
                                minWidth: '0'
                            }}>
                                <h4 style={{
                                    color: data.headingColor || '#333333',
                                    fontSize: appendPx(data.headingSize || 18),
                                    fontWeight: data.headingWeight || '600',
                                    fontFamily: data.headingFont || fonts.globalBigFont,
                                    margin: '0',
                                    padding: '0',
                                    textAlign: layoutConfig.alignment === 'center' ? 'center' : 'left',
                                    width: '100%'
                                }}>
                                    Contact
                                </h4>

                                {/* Contact Items Container */}
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '10px',
                                    alignItems: layoutConfig.alignment === 'center' ? 'center' : 'flex-start',
                                    width: '100%'
                                }}>
                                    {/* Address */}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '8px',
                                        width: '100%',
                                        minHeight: '20px'
                                    }}>
                                        <i className="fas fa-map-marker-alt" style={{
                                            color: data.textColor || '#666666', // ✅ FIXED: Grauer Fallback statt weiß
                                            fontSize: '14px',
                                            marginTop: '2px',
                                            width: '16px',
                                            textAlign: 'center',
                                            flexShrink: 0,
                                            opacity: 0.8
                                        }}></i>
                                        <div style={{ flex: 1, minWidth: '0' }}>
                                            {data.showAddress !== false && renderEditableText(
                                                'address',
                                                data.address,
                                                'p',
                                                {
                                                    color: data.textColor || '#666666', // ✅ FIXED: Grauer Fallback statt weiß
                                                    fontSize: appendPx(data.bodySize || 14),
                                                    fontWeight: data.bodyWeight || '400',
                                                    fontFamily: data.bodyFont || fonts.globalSmallFont,
                                                    lineHeight: '1.5',
                                                    margin: '0',
                                                    padding: '0',
                                                    textAlign: layoutConfig.alignment === 'center' ? 'center' : 'left',
                                                    wordWrap: 'break-word',
                                                    overflowWrap: 'break-word'
                                                },
                                                'Your address here'
                                            )}
                                        </div>
                                    </div>

                                    {/* Phone */}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        width: '100%',
                                        minHeight: '20px'
                                    }}>
                                        <i className="fas fa-phone" style={{
                                            color: data.textColor || '#666666', // ✅ FIXED: Grauer Fallback statt weiß
                                            fontSize: '14px',
                                            width: '16px',
                                            textAlign: 'center',
                                            flexShrink: 0,
                                            opacity: 0.8
                                        }}></i>
                                        <div style={{ flex: 1, minWidth: '0' }}>
                                            {data.showPhone !== false && renderEditableText(
                                                'phone',
                                                data.phone,
                                                'p',
                                                {
                                                    color: data.linkColor || '#cccccc',
                                                    fontSize: appendPx(data.bodySize || 14),
                                                    fontWeight: data.bodyWeight || '400',
                                                    fontFamily: data.bodyFont || fonts.globalSmallFont,
                                                    margin: '0',
                                                    padding: '0',
                                                    textAlign: layoutConfig.alignment === 'center' ? 'center' : 'left',
                                                    lineHeight: '1.4'
                                                },
                                                '+1 (555) 123-4567'
                                            )}
                                        </div>
                                    </div>

                                    {/* Email */}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        width: '100%',
                                        minHeight: '20px'
                                    }}>
                                        <i className="fas fa-envelope" style={{
                                            color: data.textColor || '#666666', // ✅ FIXED: Grauer Fallback statt weiß
                                            fontSize: '14px',
                                            width: '16px',
                                            textAlign: 'center',
                                            flexShrink: 0,
                                            opacity: 0.8
                                        }}></i>
                                        <div style={{ flex: 1, minWidth: '0' }}>
                                            {data.showEmail !== false && renderEditableText(
                                                'email',
                                                data.email,
                                                'p',
                                                {
                                                    color: data.linkColor || '#cccccc',
                                                    fontSize: appendPx(data.bodySize || 14),
                                                    fontWeight: data.bodyWeight || '400',
                                                    fontFamily: data.bodyFont || fonts.globalSmallFont,
                                                    margin: '0',
                                                    padding: '0',
                                                    textAlign: layoutConfig.alignment === 'center' ? 'center' : 'left',
                                                    lineHeight: '1.4',
                                                    wordBreak: 'break-all'
                                                },
                                                'contact@yourcompany.com'
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Social Links */}
                        {(data.showSocialLinks && layoutConfig.showSocialLinks) && (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '16px',
                                alignItems: layoutConfig.alignment === 'center' ? 'center' : 'flex-start'
                            }}>
                                <h4 style={{
                                    color: data.headingColor || '#333333',
                                    fontSize: appendPx(data.headingSize || 18),
                                    fontWeight: data.headingWeight || '600',
                                    fontFamily: data.headingFont || fonts.globalBigFont,
                                    marginBottom: '0'
                                }}>
                                    Follow Us
                                </h4>
                                {renderSocialLinks()}
                            </div>
                        )}
                    </div>

                        {/* Navigation Categories */}
                        {(data.showNavigation && layoutConfig.showNavigation) && renderCategories()}
                    </div>
                )}

                {/* Bottom Bar */}
                {(data.showBottomBar && layoutConfig.showBottomBar) && (
                    <div style={{
                        backgroundColor: data.bottomBarBackground || '#111111',
                        padding: `${data.bottomBarPadding || 20}px ${data.paddingX || 40}px`,
                        borderTop: '1px solid rgba(255, 255, 255, 0.05)'
                    }}>
                        <div style={{
                            maxWidth: '1200px',
                            margin: '0 auto',
                            display: 'flex',
                            flexDirection: isMobileLayout ? 'column' : 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '16px'
                        }}>
                            {data.showCopyrightText !== false && renderEditableText(
                                'copyrightText',
                                data.copyrightText,
                                'p',
                                {
                                    color: data.bottomBarTextColor || '#888888',
                                    fontSize: appendPx((data.bodySize || 14) - 1),
                                    fontWeight: data.bodyWeight || '400',
                                    fontFamily: data.bodyFont || fonts.globalSmallFont,
                                    marginBottom: '0',
                                    textAlign: isMobileLayout ? 'center' : 'left'
                                },
                                '© 2025 Your Company. All rights reserved.'
                            )}

                            {data.showLegalLinks && data.legalLinks && (
                                <div style={{
                                    display: 'flex',
                                    gap: '20px',
                                    flexWrap: 'wrap',
                                    justifyContent: isMobileLayout ? 'center' : 'flex-end'
                                }}>
                                    {data.legalLinks.map((link, index) => (
                                        <a
                                            key={index}
                                            href={link.url}
                                            style={{
                                                color: data.bottomBarTextColor || '#888888',
                                                fontSize: appendPx((data.bodySize || 14) - 1),
                                                fontWeight: data.bodyWeight || '400',
                                                fontFamily: data.bodyFont || fonts.globalSmallFont,
                                                textDecoration: 'none',
                                                transition: 'color 0.3s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.color = data.linkHoverColor || '#000000' // ✅ FIXED: Schwarzer Fallback für bessere Lesbarkeit;
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.color = data.bottomBarTextColor || '#888888';
                                            }}
                                        >
                                            {link.text}
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </footer>
        </>
    );
}

export default FancyFooter;
